import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { notifyError, notifySuccess } from '../../utils/helper';
import { validateName, validateEmail } from '../helper/validators';
import { createAgency, updateAgency } from '../../requests';
import { startLoader, stopLoader } from '../../_store/reducers/loaderReducer';
import inviteStyles from '../../_css/Invite.module.css';
import { Card } from '../UI';
import { toggleRefresh } from '../../_store/reducers/refreshReducer';

const API_FIELD_LABELS = {
  po_number: 'PO number',
  admin_password: 'Admin password',
  admin_email: 'Admin email',
  admin_full_name: 'Admin full name',
  admin_phone: 'Admin phone',
  name: 'Agency name',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
};

function formatAgencyApiError(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || 'Something went wrong';
  const issues = data.error;
  if (Array.isArray(issues) && issues.length > 0) {
    const parts = issues.map((issue) => {
      const key = Array.isArray(issue.path) ? issue.path[0] : '';
      const label = (key && API_FIELD_LABELS[key]) || (key ? String(key).replace(/_/g, ' ') : 'Field');
      const msg = issue.message?.trim();
      return msg ? `${label}: ${msg}` : label;
    });
    const head = data.message?.trim();
    return head ? `${head}: ${parts.join('; ')}` : parts.join('; ');
  }
  return data.message || 'Something went wrong';
}

const AddAgencyModal = ({ onClose, data = null, stats = null, admins = [], isView = false }) => {
  const navigate = useNavigate();
  const isCreate = !data;
  const isEditMode = !!data;
  const editableInEditMode = ['agencyName', 'fullAddress', 'poNumber', 'poNumberNext', 'adminEmail', 'adminFullName'];
  const isFieldEditable = (field) => !isEditMode || editableInEditMode.includes(field);

  const [primaryAdminId, setPrimaryAdminId] = useState(null);
  const [formData, setFormData] = useState({
    agencyName: "",
    fullAddress: "",
    poNumber: "",
    poNumberNext: "",
    adminEmail: "",
    adminPassword: "",
    adminFullName: "",
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const defaultAdminData = () => {
    if (isView) {
      const leastIdAdmin = admins.reduce((min, admin) => admin.id < min.id ? admin : min, admins[0]);
      setPrimaryAdminId(leastIdAdmin?.id ?? null);
      setFormData(prev => ({ ...prev, adminEmail: leastIdAdmin?.email ?? '', adminPassword: leastIdAdmin?.password ?? '', adminFullName: leastIdAdmin?.full_name ?? '' }));
    }
  }

  useEffect(() => {

    if (data) {
      setFormData({
        agencyName: data?.agencyName ?? data?.name ?? '',
        fullAddress: data?.fullAddress ?? data?.address ?? '',
        poNumber: data?.poNumber ?? data?.po_number ?? '',
        poNumberNext: data?.poNumberNext ?? data?.po_number_next ?? '',
        adminEmail: data?.adminEmail ?? '',
        adminPassword: data?.adminPassword ?? '',
        adminFullName: data?.adminFullName ?? '',
      });

      defaultAdminData();
    }

  }, [data]);

  const dispatch = useDispatch();

  const resetFields = () => {
    setFormData({
      agencyName: "",
      fullAddress: "",
      poNumber: "",
      poNumberNext: "",
      adminEmail: "",
      adminPassword: "",
      adminFullName: "",
    });
  }

  const validateCreateForm = () => {
    if (!formData.agencyName?.trim()) {
      notifyError('Please enter agency name');
      return false;
    }
    //minimum 3 characters
    if (formData.agencyName.length < 3) {
      notifyError('Please enter a valid agency name with at least 3 characters');
      return false;
    }

    if (!validateName(formData.agencyName)) {
      notifyError('Please enter a valid agency name');
      return false;
    }

    if (!formData.fullAddress?.trim()) {
      notifyError('Please enter address');
      return false;
    }
    if (!formData.adminEmail?.trim()) {
      notifyError('Please enter admin email');
      return false;
    }
    if (!validateEmail(formData.adminEmail)) {
      notifyError('Please enter a valid admin email address');
      return false;
    }
    // if (!formData.adminPassword) {
    //   notifyError('Please enter admin password');
    //   return false;
    // }
    // if (formData.adminPassword.length < 8) {
    //   notifyError('Admin password must be at least 8 characters');
    //   return false;
    // }
    if (!formData.adminFullName?.trim()) {
      notifyError('Please enter admin full name');
      return false;
    }
    if (formData.adminFullName.length < 3) {
      notifyError('Please enter a valid admin full name with at least 3 characters');
      return false;
    }
    if (!validateName(formData.adminFullName)) {
      notifyError('Please enter a valid admin full name');
      return false;
    }
    return true;
  };

  const validateUpdateForm = () => {
    if (!formData.agencyName?.trim()) {
      notifyError('Please enter agency name');
      return false;
    }
    if (formData.agencyName.length < 3) {
      notifyError('Please enter a valid agency name with at least 3 characters');
      return false;
    }
    if (!validateName(formData.agencyName)) {
      notifyError('Please enter a valid agency name');
      return false;
    }
    if (!formData.fullAddress?.trim()) {
      notifyError('Please enter address');
      return false;
    }
    if (!formData.adminFullName?.trim()) {
      notifyError('Please enter admin full name');
      return false;
    }
    if (formData.adminFullName.length < 3) {
      notifyError('Please enter a valid admin full name with at least 3 characters');
      return false;
    }
    if (!validateName(formData.adminFullName)) {
      notifyError('Please enter a valid admin full name');
      return false;
    }
    if (!formData.adminEmail?.trim()) {
      notifyError('Please enter admin email');
      return false;
    }
    if (!validateEmail(formData.adminEmail)) {
      notifyError('Please enter a valid admin email address');
      return false;
    }
    return true;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isCreate && !validateCreateForm()) return;

    if (isEditMode && !validateUpdateForm()) return;

    dispatch(startLoader());
    try {
      if (data) {
        const updatePayload = {
          id: data?.id,
          name: formData.agencyName?.trim(),
          address: formData.fullAddress?.trim(),
          po_number: formData.poNumber?.trim() || '',
          po_number_next: formData.poNumberNext?.trim() || '',
          ...(primaryAdminId && {
            admin_id: primaryAdminId,
            admin_full_name: formData.adminFullName?.trim(),
            admin_email: formData.adminEmail?.trim(),
          }),
        };
        const response = await updateAgency(updatePayload);
        if (response?.status === 200) {
          dispatch(toggleRefresh());
          notifySuccess(response?.data?.message || 'Agency updated successfully');
          resetFields();
          onClose?.();
        }
      } else {
        const payload = {
          name: formData.agencyName.trim(),
          address: formData.fullAddress?.trim() || '',
          phone: '', // agencies.phone has no DB default and can't be null; phone is no longer collected on this form
          po_number: formData.poNumber?.trim() || '',
          po_number_next: formData.poNumberNext?.trim() || '',
          admin_email: formData.adminEmail.trim(),
          admin_password: formData.adminPassword,
          admin_full_name: formData.adminFullName.trim(),
        };
        const response = await createAgency(payload);
        if (response?.status === 200) {
          dispatch(toggleRefresh());
          notifySuccess(response?.data?.message || 'Agency created successfully');
          resetFields();
          onClose?.();
        }
      }
    } catch (error) {
      notifyError(formatAgencyApiError(error));
    } finally {
      dispatch(stopLoader());
    }
  }

  const handleViewAdmins = () => {
    onClose?.();
    navigate('/admins', {
      state: {
        admins,
        returnToAgencyModal: true,
        agencyData: data ?? null,
        agencyStats: stats ?? null,
      },
    });
  }

  return (
    <div className={inviteStyles.page}>
      <div className={inviteStyles.headerRow}>
        <div className={inviteStyles.titleBlock}>
          <h1>
            {data ? 'Agency Details' : 'Create New Agency'}
          </h1>
          <p>
            {data
              ? 'Update agency and admin details'
              : 'Fill the form to create a new agency. Agency admin will receive an email with link to set their password'}
          </p>
        </div>
        <button
          type="button"
          className={inviteStyles.secondaryBtn}
          onClick={() => onClose?.()}
        >
          ← Back to List
        </button>
      </div>

      <Card>
        <form onSubmit={handleSubmit}>
          {data?.id && (
            <div className={inviteStyles.formField} style={{ marginBottom: 16 }}>
              <label className={inviteStyles.label}>Agency ID</label>
              <input
                type="text"
                value={data?.agency_code ?? `MPSA-${data.id}`}
                readOnly
                disabled
                className={`${inviteStyles.input} ${inviteStyles.inputReadOnly}`}
                style={{ cursor: 'not-allowed' }}
              />
            </div>
          )}
          <div className={inviteStyles.formField}>
            <label htmlFor="agencyName" className={inviteStyles.label}>
              Agency Name
              <span className={inviteStyles.labelRequired}> *</span>
            </label>
            <input
              id="agencyName"
              type="text"
              value={formData.agencyName}
              onChange={(e) => handleChange('agencyName', e.target.value)}
              placeholder="Enter agency name"
              className={`${inviteStyles.input} ${isEditMode && !isFieldEditable('agencyName') ? inviteStyles.inputReadOnly : ''}`}
              readOnly={isEditMode && !isFieldEditable('agencyName')}
              style={{ cursor: isEditMode && !isFieldEditable('agencyName') ? 'not-allowed' : 'auto' }}
              required
              minLength={3}
              maxLength={255}
            />
            <div style={{ fontSize: 12, color: '#6b6b6b', marginTop: 5 }}>
              Must be unique. For a separate team with its own contract, add a team name, e.g. 'Mediaplus Germany - Team Search'.
            </div>
          </div>

          <div className={inviteStyles.formSpacer}>
            <label htmlFor="fullAddress" className={inviteStyles.label}>
              Full Address
              <span className={inviteStyles.labelRequired}> *</span>
            </label>
            <textarea
              id="fullAddress"
              value={formData.fullAddress}
              onChange={(e) => handleChange('fullAddress', e.target.value)}
              placeholder="Enter complete address"
              rows={2}
              className={`${inviteStyles.textarea} ${isEditMode && !isFieldEditable('fullAddress') ? inviteStyles.inputReadOnly : ''}`}
              readOnly={isEditMode && !isFieldEditable('fullAddress')}
              style={{ cursor: isEditMode && !isFieldEditable('fullAddress') ? 'not-allowed' : 'auto' }}
              required
              minLength={3}
              maxLength={150}
            />
          </div>

          <div className={inviteStyles.formGrid2}>
            <div className={inviteStyles.formField}>
              <label htmlFor="poNumber" className={inviteStyles.label}>
                PO Number (Current Fiscal Year)
              </label>
              <input
                id="poNumber"
                type="text"
                value={formData.poNumber}
                onChange={(e) => handleChange('poNumber', e.target.value)}
                placeholder="PO-123456"
                className={`${inviteStyles.input} ${isEditMode && !isFieldEditable('poNumber') ? inviteStyles.inputReadOnly : ''}`}
                readOnly={isEditMode && !isFieldEditable('poNumber')}
                style={{ cursor: isEditMode && !isFieldEditable('poNumber') ? 'not-allowed' : 'auto' }}
                maxLength={255}
              />
            </div>
            <div className={inviteStyles.formField}>
              <label htmlFor="poNumberNext" className={inviteStyles.label}>
                PO Number (Next Fiscal Year)
              </label>
              <input
                id="poNumberNext"
                type="text"
                value={formData.poNumberNext}
                onChange={(e) => handleChange('poNumberNext', e.target.value)}
                placeholder="PO-123456"
                className={`${inviteStyles.input} ${isEditMode && !isFieldEditable('poNumberNext') ? inviteStyles.inputReadOnly : ''}`}
                readOnly={isEditMode && !isFieldEditable('poNumberNext')}
                style={{ cursor: isEditMode && !isFieldEditable('poNumberNext') ? 'not-allowed' : 'auto' }}
                maxLength={255}
              />
            </div>
          </div>

          <div className={inviteStyles.sectionDivider}>
            <div className={inviteStyles.sectionTitle}>Agency Admin Details</div>

            {isView && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button type="button" onClick={handleViewAdmins} className={inviteStyles.secondaryBtn}>
                  View All Admins
                </button>
              </div>
            )}

            <div className={inviteStyles.formGrid2}>
              <div className={inviteStyles.formField}>
                <label htmlFor="adminEmail" className={inviteStyles.label}>
                  Admin Email Address
                  <span className={inviteStyles.labelRequired}> *</span>
                </label>
                <input
                  id="adminEmail"
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => handleChange('adminEmail', e.target.value)}
                  placeholder="admin@example.com"
                  className={`${inviteStyles.input} ${isEditMode && !isFieldEditable('adminEmail') ? inviteStyles.inputReadOnly : ''}`}
                  readOnly={isEditMode && !isFieldEditable('adminEmail')}
                  style={{ cursor: isEditMode && !isFieldEditable('adminEmail') ? 'not-allowed' : 'auto' }}
                  required
                />
              </div>
              <div className={inviteStyles.formField}>
                <label htmlFor="adminFullName" className={inviteStyles.label}>
                  Admin Full Name
                  <span className={inviteStyles.labelRequired}> *</span>
                </label>
                <input
                  id="adminFullName"
                  type="text"
                  value={formData.adminFullName}
                  onChange={(e) => handleChange('adminFullName', e.target.value)}
                  placeholder="John Doe"
                  className={`${inviteStyles.input} ${isEditMode && !isFieldEditable('adminFullName') ? inviteStyles.inputReadOnly : ''}`}
                  readOnly={isEditMode && !isFieldEditable('adminFullName')}
                  style={{ cursor: isEditMode && !isFieldEditable('adminFullName') ? 'not-allowed' : 'auto' }}
                  required
                />
              </div>
            </div>
          </div>

          {stats && (
            <>
              <div className={inviteStyles.sectionDivider}>
                <div className={inviteStyles.sectionTitle}>Agency Stats</div>
              </div>
              <div className={`${inviteStyles.formGrid3}`} style={{ marginTop: 0 }}>
                <div className={inviteStyles.statMini}>
                  <p className={inviteStyles.label} style={{ marginBottom: 4 }}>
                    Total Admins
                  </p>
                  <p className={inviteStyles.statMiniValue}>{stats.totalAdmins ?? 0}</p>
                </div>
                <div className={inviteStyles.statMini}>
                  <p className={inviteStyles.label} style={{ marginBottom: 4 }}>
                    Active Admins
                  </p>
                  <p className={inviteStyles.statMiniValue}>{stats.totalActiveAdmins ?? 0}</p>
                </div>
                <div className={inviteStyles.statMini}>
                  <p className={inviteStyles.label} style={{ marginBottom: 4 }}>
                    Total Brands
                  </p>
                  <p className={inviteStyles.statMiniValue}>{stats.totalBrands ?? 0}</p>
                </div>
                <div className={inviteStyles.statMini}>
                  <p className={inviteStyles.label} style={{ marginBottom: 4 }}>
                    Active Brands
                  </p>
                  <p className={inviteStyles.statMiniValue}>{stats.totalActiveBrands ?? 0}</p>
                </div>
                <div className={inviteStyles.statMini}>
                  <p className={inviteStyles.label} style={{ marginBottom: 4 }}>
                    Total Projects
                  </p>
                  <p className={inviteStyles.statMiniValue}>{stats.totalProjects ?? 0}</p>
                </div>
                <div className={inviteStyles.statMini}>
                  <p className={inviteStyles.label} style={{ marginBottom: 4 }}>
                    Active Projects
                  </p>
                  <p className={inviteStyles.statMiniValue}>{stats.totalActiveProjects ?? 0}</p>
                </div>
              </div>
            </>
          )}

          <div className={inviteStyles.formActions}>
            <button type="submit" className={inviteStyles.submitBtn}>
              Save Agency
            </button>
            {!data && (
              <button type="button" onClick={resetFields} className={inviteStyles.secondaryBtn}>
                Reset
              </button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
}

export default AddAgencyModal
